'use client'

import { Box, BoxProps, Chip, styled, Typography } from '@mui/material'
import { FreeMode } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

// Import Swiper styles
import { Icon } from '@/components'
import { useVoucherContext } from '@/features/coupon/contexts/voucher-context'
import { useMultiPromotionWithoutCart } from '@/features/coupon/hooks/use-promotion'
import { TagDiscount } from '@/features/products/components/product-detail/select-quantity'
import { useProductDetailV2State } from '@/features/products/contexts/product-detail'
import { getVND } from '@/utils/helpers'
import { useEffect, useMemo, useState } from 'react'
import 'swiper/css'
import 'swiper/css/free-mode'
import { PromotionCardSkeleton } from './loading'

const StyledSwiperSlide = styled(SwiperSlide)(({ theme }) => ({
  width: 'auto',
  flexGrow: 1,
  padding: '10px 6px 4px 6px',
}))

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => !['isSelected', 'isDisabled'].includes(prop as string),
})<{
  isSelected?: boolean
  isDisabled?: boolean
}>(({ theme, isSelected, isDisabled }) => ({
  padding: 8,
  minWidth: 188,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  border: '1px solid transparent',
  transition: 'background-color 0.3s, border-color 0.3s',
  position: 'relative',
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  background: isSelected ? '#E7F7F0' : theme.palette.background.default,
  borderColor: isSelected ? '#17B26A' : 'rgba(0, 0, 0, 0.1)',
  opacity: isDisabled ? 0.9 : 1,
  '& .action-btn': {
    borderColor: isSelected ? theme.palette.primary.main : 'transparent',
  },
  '&:hover .action-btn': {
    borderColor: theme.palette.primary.main,
  },
  [theme.breakpoints.up('lg')]: {
    padding: 12,
    borderRadius: 6,
  },
  [theme.breakpoints.down('lg')]: {
    borderRadius: 4,
    paddingTop: 14,
    minWidth: 144,
  },
  '&:after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: 24,
    height: 24,
    borderRadius: '0 0 0 999px',
    backgroundColor: '#17B26A',
    opacity: isSelected ? 1 : 0,
    transition: 'opacity 0.3s',
  },
}))

const LabelOffer = ({ num }: { num: number }) => {
  if (!num || num <= 0) return null
  return (
    <Chip
      label={`Offer #${num}`}
      size="small"
      sx={{
        bgcolor: 'error.main',
        color: 'common.white',
        fontWeight: 500,
        fontSize: '12px',
        height: 'auto',
        py: 0.25,
        px: 0.75,
        borderRadius: '12px',
        position: 'absolute',
        top: 0,
        left: {
          xs: 8,
          lg: 12,
        },
        transform: 'translateY(-50%)',
        '& .MuiChip-label': {
          px: 0,
          borderRadius: '12px',
        },
      }}
    />
  )
}
const getPercentDiscount = (originalPrice: number | undefined, discountedPrice: number) => {
  if (!originalPrice) return 0
  if (originalPrice <= 0) return 0
  const discount = ((originalPrice - discountedPrice) / originalPrice) * 100
  return Math.round(discount)
}

type Props = {
  props?: Omit<BoxProps, 'children'>
}

export const ListComboProduct = ({ props }: Props) => {
  const { tempQuantityWithCoupon, product, productSelected, setQuantity } = useProductDetailV2State()
  const { listPromotionProps, caculatedQuantity } = useVoucherContext()

  // State quản lý active coupon tạm thời cho UI responsive
  const [tempActiveQuantity, setTempActiveQuantity] = useState<number | null>(null)

  // Sync tempActiveQuantity với caculatedQuantity
  useEffect(() => {
    if (caculatedQuantity) {
      setTempActiveQuantity(caculatedQuantity)
    }
  }, [caculatedQuantity])

  const dataPriceSelectedProduct = useMemo(() => {
    return productSelected?.product?.price_range?.maximum_price ?? product?.price_range?.maximum_price
  }, [productSelected?.product?.price_range?.maximum_price, product?.price_range?.maximum_price])

  const { data, isLoading } = useMultiPromotionWithoutCart({
    ...listPromotionProps,
    enabled: true,
    listQuantity: tempQuantityWithCoupon?.map((item) => item?.qty) || [],
  })

  const isAllResponseError = useMemo(() => {
    if (!data || data.length === 0) return false
    return data.every((item) => item?.status === 'error' || !item?.data)
  }, [data])

  // const isLargerThanTheFinalVoucher = useMemo(() => {
  //   if (!tempQuantityWithCoupon?.[tempQuantityWithCoupon.length - 1]?.qty) return false
  //   return currentItemsInCart >= tempQuantityWithCoupon?.[tempQuantityWithCoupon.length - 1]?.qty
  // }, [currentItemsInCart, tempQuantityWithCoupon])

  // const theLastOffer = useMemo(() => {
  //   if (!tempQuantityWithCoupon || tempQuantityWithCoupon.length === 0) return null
  //   return tempQuantityWithCoupon?.filter((item) => item?.qty <= currentItemsInCart).pop() || null
  // }, [currentItemsInCart, tempQuantityWithCoupon])

  const getCurrentPromotionData = (index: number) => {
    try {
      if (data && data?.length > index) {
        return data?.[index]?.data
      }
      return null
    } catch (error) {
      return null
    }
  }

  if (!tempQuantityWithCoupon || tempQuantityWithCoupon.length === 0 || isAllResponseError) {
    return null
  }

  return (
    <Box
      {...props}
      sx={{
        padding: 0,
        borderRadius: 2,
        ...props?.sx,
      }}
    >
      {isLoading && <PromotionCardSkeleton />}

      {!isLoading && (
        <Box overflow={'hidden'} borderRadius={2}>
          <Swiper
            modules={[FreeMode]}
            slidesPerView="auto"
            freeMode={true}
            style={{ padding: '0', margin: '0 -6px' }}
          >
            {tempQuantityWithCoupon?.map((item, index) => {
              const dataPrice = getCurrentPromotionData(index)
              // const isLargerThanTheFinalVoucher = item?.qty > currentItemsInCart
              // const isLastMatchedOffers = item?.qty === theLastOffer?.qty
              // const isActive = item?.qty === tempActiveQuantity || isLastMatchedOffers
              const isLargerThanTheFinalVoucher = true
              const isActive = item?.qty === tempActiveQuantity
              if (!dataPrice) return null

              return (
                <StyledSwiperSlide key={index}>
                  {/* <Tooltip title={isLastMatchedOffers ? 'Đã áp dụng' : ''} arrow placement="top"> */}
                  <StyledBox
                    onClick={() => {
                      // Kiểm tra điều kiện trước khi active
                      if (!isLargerThanTheFinalVoucher) return

                      //  Cập nhật UI ngay lập tứ
                      setTempActiveQuantity(item?.qty)

                      // cập nhật state chính
                      // setQuantity(item?.qty - currentItemsInCart)
                      setQuantity(item?.qty)
                    }}
                    isSelected={isActive}
                    isDisabled={!isLargerThanTheFinalVoucher}
                  >
                    {/* decor */}
                    <Icon.CheckWithoutBg
                      sx={{
                        position: 'absolute',
                        top: '3px',
                        right: '3px',
                        fontSize: '12px',
                        color: 'white',
                        zIndex: 1,
                        opacity: isActive ? 1 : 0,
                        transition: 'opacity 0.3s',
                      }}
                    />
                    {/* Header: Offer Badge + Text */}
                    <LabelOffer num={index + 1} />
                    <Typography
                      sx={{
                        color: 'text.primary',
                        fontWeight: 300,
                        fontSize: '14px',
                        lineHeight: 1.5,
                      }}
                    >
                      Mua{' '}
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {item?.qty}
                      </Box>{' '}
                      sản phẩm giá chỉ:
                    </Typography>
                    {/* Price Section with Discount Badge */}
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <Typography
                        component={'span'}
                        sx={{
                          fontSize: { xs: '1rem', md: '1.125rem' },
                          fontWeight: 600,
                          lineHeight: 1,
                        }}
                      >
                        {getVND(dataPrice?.single_price, 'Đ')}{' '}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            fontWeight: 300,
                          }}
                        >
                          /sản phẩm
                        </Typography>
                      </Typography>

                      {/* Discount Badge - positioned absolutely */}
                      <TagDiscount
                        tagProps={{
                          sx: {
                            position: 'static',
                            transform: 'unset',
                            display: 'inline-block',
                            ml: 0.5,
                            padding: '1px 4px',
                          },
                          component: 'span',
                        }}
                        theDiscountPercent={getPercentDiscount(
                          dataPriceSelectedProduct?.final_price?.value,
                          dataPrice?.single_price
                        )}
                      />
                    </Box>
                  </StyledBox>
                  {/* </Tooltip> */}
                </StyledSwiperSlide>
              )
            })}
          </Swiper>
        </Box>
      )}
    </Box>
  )
}
